<template>
  <b-modal
    id="manage-labels-modal"
    :title="$t('Manage Labels')"
    size="lg"
    hide-footer
  >
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-baseline">
      <h4>{{ $t('Labels') }}</h4>
      <b-button
        v-b-modal.create-edit-label
        variant="primary"
      >
        {{ $t('Add Label') }}
      </b-button>
    </div>

    <!-- Labels Table -->
    <b-table
      :items="GET_CURRENT_BOARD_LABELS"
      :fields="fields"
      responsive
      show-empty
      class="mt-2 pb-3"
    >
      <template #head()="{ label }">
        <span class="text-capitalize">{{ label }}</span>
      </template>

      <template #cell(name)="{ item }">
        <b-badge :style="{ backgroundColor: item.color }">
          {{ item.name }}
        </b-badge>
      </template>

      <template #cell(description)="{ item }">
        <div class="d-flex justify-content-between align-items-center">
          <span>{{ item.description || '-' }}</span>

          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="14"
              />
            </template>

            <b-dropdown-item
              v-b-modal.create-edit-label
              size="sm"
              @click="form = { ...item }"
            >
              <feather-icon icon="EditIcon" />
              <span class="ml-1">{{ $t('Edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-b-modal.confirm-delete-label-modal
              size="sm"
              variant="danger"
              @click="form = { ...item }"
            >
              <feather-icon icon="Trash2Icon" />
              <span class="ml-1">{{ $t('Delete') }}</span>
            </b-dropdown-item></b-dropdown>
        </div>
      </template>

      <!-- <template #cell(actions)="{ item }">
        <b-button
          v-b-modal.create-edit-label
          size="sm"
          variant="flat-warning"
          @click="form = { ...item }"
        >
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button
          v-b-modal.confirm-delete-label-modal
          size="sm"
          variant="flat-danger"
          @click="form = { ...item }"
        >
          <feather-icon icon="Trash2Icon" />
        </b-button>
      </template> -->
    </b-table>

    <!-- Confirm Delete Label Modal -->
    <confirm-modal
      modal="confirm-delete-label-modal"
      :title="$t('Delete Label')"
      :description="`Are you sure you want to delete the label '${form.name}'?`"
      :ok-title="$t('Confirm')"
      ok-variant="danger"
      @confirm="deleteLabel(form.uuid)"
    />

    <!-- Create / Edit Label Modal -->
    <b-modal
      id="create-edit-label"
      :title="form.uuid ? $t('Edit Label') : $t('Create Label')"
      :ok-title="form.uuid ? $t('Update') : $t('Add')"
      :ok-disabled="isProcessing"
      :cancel-disabled="isProcessing"
      @ok="handleSubmit"
      @hidden="form = {}"
    >
      <b-overlay :show="isProcessing">
        <validation-observer ref="labelFormObserver">
          <div class="d-flex justify-content-between">
            <validation-provider
              v-slot="{ errors }"
              name="label name"
              rules="required"
              class="w-75 pr-2"
            >
              <b-form-group
                :label="$t('Name')"
                label-for="label-name-input"
              >
                <b-form-input
                  id="label-name-input"
                  v-model="form.name"
                  :placeholder="$t('Enter label name')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <b-form-group
              :label="$t('Color')"
              label-for="label-color-input"
              class="w-25"
            >
              <b-form-input
                id="label-color-input"
                v-model="form.color"
                type="color"
              />
            </b-form-group>
          </div>

          <b-form-group
            :label="$t('Description')"
            label-for="label-description-input"
          >
            <b-form-textarea
              id="label-description-input"
              v-model="form.description"
              rows="3"
              :placeholder="$t('Enter label description')"
            />
          </b-form-group>
        </validation-observer>
      </b-overlay>
    </b-modal>
  </b-modal>
</template>

<script>
import {
  BModal, BTable, BButton, BOverlay, BFormGroup, BFormInput, BBadge, BFormTextarea,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import ConfirmModal from '@/views/Wallet/components/reusables/ConfirmModal.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'LabelManager',
  components: {
    BModal,
    BTable,
    BButton,
    BFormGroup,
    BFormTextarea,
    BFormInput,
    BOverlay,
    BBadge,
    BDropdown,
    BDropdownItem,
    ConfirmModal,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      form: {},
      fields: [
        { key: 'name', label: this.$t('Name') },
        { key: 'description', label: this.$t('Description') },
      ],
      isProcessing: false,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_CURRENT_BOARD_LABELS']),
    projectUid() {
      return this.$store.state.project.selectedProject
    },
    boardUid() {
      return this.base58Converter(this.$route.params.boardUid, false)
    },
  },
  methods: {
    ...mapActions('boards', ['fetchBoardLabels', 'createBoardLabel', 'updateBoardLabel', 'deleteBoardLabel']),
    async handleSubmit(bvModal) {
      bvModal.preventDefault()
      this.$refs.labelFormObserver.validate().then(async success => {
        if (success) {
          this.isProcessing = true
          try {
            const { uuid, __typename, ...input } = this.form
            if (!input.color) input.color = '#000000'

            if (uuid) {
              const response = await this.updateBoardLabel({
                projectUid: this.projectUid,
                boardUid: this.boardUid,
                labelUid: uuid,
                input,
              })
              this.showSuccess(response.message)
            } else {
              const response = await this.createBoardLabel({
                projectUid: this.projectUid,
                boardUid: this.boardUid,
                input,
              })
              await this.fetchBoardLabels(this.boardUid)
              this.showSuccess(response.message)
            }

            this.$bvModal.hide('create-edit-label')
          } catch (error) {
            this.showError(error.message)
          } finally {
            this.isProcessing = false
          }
        }
      })
    },
    async deleteLabel(label) {
      try {
        const response = await this.deleteBoardLabel({
          projectUid: this.projectUid,
          boardUid: this.boardUid,
          labelUid: label,
        })
        this.$emit('refresh')
        this.showSuccess(response.message)
        this.form = {}
      } catch (error) {
        this.showError(error.message)
      }
    },
  },
}
</script>
